import { useState, useEffect, useCallback } from "react";
import {
  Row,
  Col,
  Card,
  Table,
  Tag,
  Button,
  Select,
  DatePicker,
  Space,
  Typography,
  Tooltip,
  Badge,
  message,
  Segmented,
} from "antd";
import {
  ReloadOutlined,
  AuditOutlined,
  UserOutlined,
  ClockCircleOutlined,
  PlusCircleOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  LoginOutlined,
  LogoutOutlined,
  InfoCircleOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import * as XLSX from "xlsx";
import Utilities from "../../dependencies/custom/react-utilities";
import domainSettings from "../../dependencies/custom/settings";

dayjs.extend(relativeTime);

const { RangePicker } = DatePicker;
const { Text } = Typography;

// ─── Helpers to turn raw log data into human-readable descriptions ───

const ACTION_VERB = {
  create: "Created",
  update: "Updated",
  delete: "Deleted",
  access: "Viewed",
};

const ACTION_ICON = {
  create: <PlusCircleOutlined />,
  update: <EditOutlined />,
  delete: <DeleteOutlined />,
  access: <EyeOutlined />,
};

const ACTION_COLORS = {
  access: "default",
  create: "green",
  update: "orange",
  delete: "red",
};

const METHOD_COLORS = {
  GET: "cyan",
  POST: "green",
  PUT: "orange",
  PATCH: "gold",
  DELETE: "red",
};

/**
 * Converts a URL path segment into a readable label.
 *  "preschool-remark" → "Preschool Remark"
 *  "academic-years"   → "Academic Years"
 */
const segmentToLabel = (segment) =>
  segment.replace(/[-_]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

/**
 * Known top-level path prefixes → friendly module names.
 */
const MODULE_LABELS = {
  academics: "Academics",
  enrollment: "Enrollment",
  finance: "Finance",
  rbac: "User Management",
  bootstrap: "System Settings",
  transport: "Transportation",
  bot: "Chatbot",
  auth: "Authentication",
  audit: "Audit",
  accounts: "Accounts",
};

/**
 * Build a human-readable description from the action, method, and path.
 *
 * Examples:
 *   POST  /academics/inspections        → "Created a new Inspection in Academics"
 *   GET   /enrollment/terms             → "Viewed Terms in Enrollment"
 *   DELETE /rbac/roles/user             → "Deleted User Role in User Management"
 *   PATCH /academics/preschool-grade/5  → "Updated Preschool Grade in Academics"
 */
const buildDescription = (action, changes) => {
  const method = changes?.method;
  const path = changes?.path || "";
  const event = changes?.event;

  // Special events
  if (event === "login") return "Logged in";
  if (event === "logout") return "Logged out";

  const segments = path.split("/").filter(Boolean);
  if (segments.length === 0)
    return action
      ? `${ACTION_VERB[action?.toLowerCase()] || action}`
      : "Unknown action";

  // First segment is the module prefix (academics, enrollment, etc.)
  const moduleKey = segments[0];
  const moduleName = MODULE_LABELS[moduleKey] || segmentToLabel(moduleKey);

  // Find the most meaningful segment (skip the module prefix and numeric IDs)
  const meaningful = segments.slice(1).filter((s) => !/^\d+$/.test(s));
  const resourceName =
    meaningful.length > 0
      ? meaningful.map(segmentToLabel).join(" › ")
      : moduleName;

  const verb = ACTION_VERB[action?.toLowerCase()] || action || "Accessed";

  // Build the sentence
  if (meaningful.length > 0) {
    return `${verb} ${resourceName}`;
  }
  return `${verb} ${moduleName}`;
};

const statusColor = (code) => {
  if (code >= 500) return "red";
  if (code >= 400) return "orange";
  if (code >= 300) return "blue";
  return "green";
};

// ─── Component ───

const AuditTrail = () => {
  const navigate = useNavigate();

  const [roles, setRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [viewMode, setViewMode] = useState("changes"); // 'all' | 'changes'
  const [exporting, setExporting] = useState(false);

  const [filters, setFilters] = useState({
    roleID: 1,
    dateRange: null,
  });

  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 30,
    total: 0,
  });

  const fetchAuditTrail = useCallback(
    async (roleID, dateRange, page = 1, pageSize = 30) => {
      if (!roleID) {
        setLogs([]);
        setPagination((p) => ({ ...p, total: 0 }));
        return;
      }

      setLoading(true);
      try {
        const params = new URLSearchParams({
          roleID,
          page,
          page_size: pageSize,
        });

        if (dateRange?.[0]) {
          params.append("start", dateRange[0].format("YYYY-MM-DD"));
        }
        if (dateRange?.[1]) {
          params.append("end", dateRange[1].format("YYYY-MM-DD"));
        }

        const url = `${domainSettings.urls.audit.trail}?${params.toString()}`;
        const response = await Utilities.fetchWithAuth(
          "GET",
          url,
          null,
          navigate,
        );

        if (response.ok) {
          const data = response.data || response;
          if (data?.results && typeof data.count === "number") {
            setLogs(data.results);
            setPagination((p) => ({
              ...p,
              current: page,
              pageSize,
              total: data.count,
            }));
          } else {
            const list = Array.isArray(data) ? data : [];
            setLogs(list);
            setPagination((p) => ({ ...p, current: 1, total: list.length }));
          }
        } else {
          message.error("Failed to fetch audit logs");
          setLogs([]);
          setPagination((p) => ({ ...p, total: 0 }));
        }
      } catch (err) {
        message.error("Error fetching audit logs");
        console.error(err);
        setLogs([]);
        setPagination((p) => ({ ...p, total: 0 }));
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  useEffect(() => {
    fetchAuditTrail(filters.roleID, filters.dateRange, 1, pagination.pageSize);
  }, [filters.roleID, filters.dateRange, fetchAuditTrail]);

  const handleTableChange = (pag) => {
    fetchAuditTrail(
      filters.roleID,
      filters.dateRange,
      pag.current,
      pag.pageSize,
    );
  };

  useEffect(() => {
    const loadRoles = async () => {
      setRolesLoading(true);
      try {
        const url = domainSettings.urls.rbac.roles;
        const response = await Utilities.fetchWithAuth(
          "GET",
          url,
          null,
          navigate,
        );
        if (response.ok) {
          const data = response.data || response;
          const list = Array.isArray(data) ? data : data.results || [];
          setRoles(list);
        } else {
          message.error("Failed to load roles");
        }
      } catch (err) {
        message.error("Error loading roles");
        console.error(err);
      } finally {
        setRolesLoading(false);
      }
    };
    loadRoles();
  }, [navigate]);

  // ─── Export to Excel ───
  const exportToExcel = async () => {
    if (!filters.roleID) return;

    setExporting(true);
    const hide = message.loading("Fetching all records for export…", 0);

    try {
      // Fetch all pages
      const allLogs = [];
      let page = 1;
      const pageSize = 200; // max allowed by backend
      let hasMore = true;

      while (hasMore) {
        const params = new URLSearchParams({
          roleID: filters.roleID,
          page,
          page_size: pageSize,
        });
        if (filters.dateRange?.[0])
          params.append("start", filters.dateRange[0].format("YYYY-MM-DD"));
        if (filters.dateRange?.[1])
          params.append("end", filters.dateRange[1].format("YYYY-MM-DD"));

        const url = `${domainSettings.urls.audit.trail}?${params.toString()}`;
        const response = await Utilities.fetchWithAuth(
          "GET",
          url,
          null,
          navigate,
        );

        if (!response.ok) {
          message.error("Failed to fetch data for export");
          return;
        }

        const data = response.data || response;
        const results = data?.results || (Array.isArray(data) ? data : []);
        allLogs.push(...results);

        hasMore = !!data?.next;
        page += 1;
      }

      // Apply the same view mode filter
      const filtered =
        viewMode === "changes"
          ? allLogs.filter((l) => l.action?.toLowerCase() !== "access")
          : allLogs;

      if (filtered.length === 0) {
        message.warning("No records to export");
        return;
      }

      // Build rows for Excel
      const rows = filtered.map((log) => ({
        User: log.user || "—",
        Action: ACTION_VERB[log.action?.toLowerCase()] || log.action || "—",
        Description: buildDescription(log.action, log.changes),
        Method: log.changes?.method || "—",
        Path: log.changes?.path || "—",
        Status: log.changes?.status || "—",
        "IP Address": log.changes?.ip || "—",
        Date: log.timestamp ? dayjs(log.timestamp).format("YYYY-MM-DD") : "—",
        Time: log.timestamp ? dayjs(log.timestamp).format("HH:mm:ss") : "—",
      }));

      // Create workbook
      const ws = XLSX.utils.json_to_sheet(rows);

      // Set column widths for readability
      ws["!cols"] = [
        { wch: 20 }, // User
        { wch: 12 }, // Action
        { wch: 40 }, // Description
        { wch: 10 }, // Method
        { wch: 40 }, // Path
        { wch: 8 }, // Status
        { wch: 16 }, // IP Address
        { wch: 12 }, // Date
        { wch: 10 }, // Time
      ];

      const wb = XLSX.utils.book_new();
      const sheetName = selectedRoleName
        ? `Audit - ${selectedRoleName}`.slice(0, 31)
        : "Audit Trail";
      XLSX.utils.book_append_sheet(wb, ws, sheetName);

      // Generate filename
      const dateSuffix = filters.dateRange
        ? `_${filters.dateRange[0].format("YYYY-MM-DD")}_to_${filters.dateRange[1].format("YYYY-MM-DD")}`
        : `_${dayjs().format("YYYY-MM-DD")}`;
      const roleSuffix = selectedRoleName
        ? `_${selectedRoleName.replace(/\s+/g, "_")}`
        : "";
      const filename = `Audit_Trail${roleSuffix}${dateSuffix}.xlsx`;

      XLSX.writeFile(wb, filename);
      message.success(`Exported ${filtered.length} records`);
    } catch (err) {
      console.error(err);
      message.error("Export failed");
    } finally {
      hide();
      setExporting(false);
    }
  };

  // Filter logs based on view mode
  const displayedLogs =
    viewMode === "changes"
      ? logs.filter((l) => l.action?.toLowerCase() !== "access")
      : logs;

  const getActionIcon = (action, changes) => {
    const event = changes?.event;
    if (event === "login")
      return <LoginOutlined style={{ color: "#52c41a" }} />;
    if (event === "logout")
      return <LogoutOutlined style={{ color: "#8c8c8c" }} />;
    return ACTION_ICON[action?.toLowerCase()] || <InfoCircleOutlined />;
  };

  const columns = [
    {
      title: "User",
      dataIndex: "user",
      key: "user",
      width: 160,
      render: (user) => (
        <Space size={6}>
          <UserOutlined style={{ color: "#8c8c8c" }} />
          <Text strong>{user || "—"}</Text>
        </Space>
      ),
      filters: [...new Set(logs.map((l) => l.user).filter(Boolean))].map(
        (u) => ({
          text: u,
          value: u,
        }),
      ),
      onFilter: (value, record) => record.user === value,
    },
    {
      title: "Activity",
      key: "description",
      render: (_, record) => {
        const action = record.action?.toLowerCase();
        const description = buildDescription(record.action, record.changes);
        const icon = getActionIcon(record.action, record.changes);
        const color = ACTION_COLORS[action] || "default";

        return (
          <Space size={8}>
            <Tag
              color={color}
              icon={icon}
              style={{ minWidth: 90, textAlign: "center" }}
            >
              {ACTION_VERB[action] || record.action}
            </Tag>
            <Text>
              {description.replace(/^(Created|Updated|Deleted|Viewed)\s*/, "")}
            </Text>
          </Space>
        );
      },
      filters: [
        { text: "Created", value: "create" },
        { text: "Updated", value: "update" },
        { text: "Deleted", value: "delete" },
        ...(viewMode === "all" ? [{ text: "Viewed", value: "access" }] : []),
      ],
      onFilter: (value, record) => record.action?.toLowerCase() === value,
    },
    {
      title: "Status",
      key: "status",
      width: 80,
      render: (_, record) => {
        const code = record.changes?.status;
        if (!code) return "—";
        return (
          <Badge
            color={statusColor(code)}
            text={<Text style={{ fontSize: 13 }}>{code}</Text>}
          />
        );
      },
    },
    {
      title: "When",
      dataIndex: "timestamp",
      key: "timestamp",
      width: 170,
      sorter: (a, b) => new Date(b.timestamp) - new Date(a.timestamp),
      defaultSortOrder: "ascend",
      render: (ts) => {
        if (!ts) return "—";
        const d = dayjs(ts);
        return (
          <Space size={4}>
            <ClockCircleOutlined style={{ color: "#8c8c8c", fontSize: 12 }} />
            <span>
              <Text>{d.format("YYYY-MM-DD HH:mm:ss")}</Text>
              <br />
              <Text type="secondary" style={{ fontSize: 12 }}>
                {d.fromNow()}
              </Text>
            </span>
          </Space>
        );
      },
    },
  ];

  const expandedRowRender = (record) => {
    const { method, path, ip, agent, payload } = record.changes || {};
    const details = [
      {
        label: "Method",
        value: method && (
          <Tag color={METHOD_COLORS[method] || "default"}>{method}</Tag>
        ),
      },
      {
        label: "Path",
        value: path && (
          <Text code style={{ fontSize: 12 }}>
            {path}
          </Text>
        ),
      },
      {
        label: "IP Address",
        value: ip && (
          <Text style={{ fontFamily: "monospace", fontSize: 12 }}>{ip}</Text>
        ),
      },
      {
        label: "User Agent",
        value: agent && (
          <Text style={{ fontSize: 12, wordBreak: "break-word" }}>{agent}</Text>
        ),
      },
      {
        label: "Payload",
        value: payload && (
          <Text
            code
            style={{
              fontSize: 12,
              wordBreak: "break-word",
              whiteSpace: "pre-wrap",
            }}
          >
            {typeof payload === "object"
              ? JSON.stringify(payload, null, 2)
              : payload}
          </Text>
        ),
      },
    ].filter((d) => d.value);

    return (
      <Space
        direction="vertical"
        size={4}
        style={{ width: "100%", padding: "4px 0" }}
      >
        {details.map((d) => (
          <Row gutter={8} key={d.label}>
            <Col flex="100px">
              <Text type="secondary" style={{ fontSize: 12 }}>
                {d.label}:
              </Text>
            </Col>
            <Col flex="auto">{d.value}</Col>
          </Row>
        ))}
      </Space>
    );
  };

  const selectedRoleName = roles.find((r) => r.id === filters.roleID)?.name;

  return (
    <Space direction="vertical" style={{ width: "100%" }} size="large">
      {/* Filter Card */}
      <Card
        title={
          <Space>
            <AuditOutlined />
            <span>Audit Trail</span>
          </Space>
        }
        extra={
          <Space>
            <Button
              icon={<DownloadOutlined />}
              onClick={exportToExcel}
              loading={exporting}
              size="small"
            >
              Export to Excel
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={() =>
                fetchAuditTrail(
                  filters.roleID,
                  filters.dateRange,
                  pagination.current,
                  pagination.pageSize,
                )
              }
              loading={loading}
              size="small"
            >
              Refresh
            </Button>
          </Space>
        }
      >
        <Row gutter={[16, 16]} align="bottom">
          <Col xs={24} sm={12} md={8}>
            <div style={{ marginBottom: 4 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Role
              </Text>
            </div>
            <Select
              placeholder="Select a role"
              style={{ width: "100%" }}
              loading={rolesLoading}
              value={filters.roleID}
              onChange={(val) =>
                val && setFilters((f) => ({ ...f, roleID: val }))
              }
              showSearch
              optionFilterProp="children"
            >
              {roles.map((r) => (
                <Select.Option key={r.id} value={r.id}>
                  {r.name}
                </Select.Option>
              ))}
            </Select>
          </Col>

          <Col xs={24} sm={12} md={10}>
            <div style={{ marginBottom: 4 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Date Range
              </Text>
            </div>
            <RangePicker
              style={{ width: "100%" }}
              value={filters.dateRange}
              onChange={(dates) =>
                setFilters((f) => ({ ...f, dateRange: dates }))
              }
              format="YYYY-MM-DD"
              placeholder={["Start Date", "End Date"]}
              disabledDate={(d) => d && d.isAfter(dayjs(), "day")}
            />
          </Col>
        </Row>
      </Card>

      {/* Results Table */}
      <Card
        title={
          <Space>
            <span>
              Activity
              {selectedRoleName && (
                <Text
                  type="secondary"
                  style={{ fontWeight: "normal", marginLeft: 8 }}
                >
                  — {selectedRoleName}
                </Text>
              )}
            </span>
            <Tag color="blue">
              {displayedLogs.length} of {pagination.total}
            </Tag>
          </Space>
        }
        extra={
          <Segmented
            value={viewMode}
            onChange={setViewMode}
            options={[
              { label: "Changes Only", value: "changes" },
              { label: "All Activity", value: "all" },
            ]}
            size="small"
          />
        }
      >
        <Table
          dataSource={displayedLogs}
          columns={columns}
          rowKey="id"
          loading={loading}
          size="middle"
          scroll={{ x: 700 }}
          onChange={handleTableChange}
          pagination={{
            ...pagination,
            pageSizeOptions: ["10", "20", "30", "50", "100"],
            showSizeChanger: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} entries`,
          }}
          expandable={{
            expandedRowRender,
            rowExpandable: () => true,
          }}
        />
      </Card>
    </Space>
  );
};

export default AuditTrail;
